import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { classNames } from "primereact/utils";
import { useGym } from "./hooks/useGym";
import {
  HistoryFilter,
  IGymUserSearchResult,
  IGymVisitViewModel,
  getInitials,
  getMemberFullName,
} from "./models/gymModels";
import { humanizePaymentType } from "../../common/utilities/paymentUtility";
import {
  constructCashdeskHref,
  constructEditUserHref,
} from "../../common/infrastructure/routes/routePaths";
import { GymPaymentModal } from "./GymPaymentModal";
import { AppDialog } from "../../common/components/AppDialog/AppDialog";
import { UserWizardContents } from "../administration/usersAdministration/userWizard/components/UserWizardContents";
import { RangeCalendar } from "../../common/components/forms/inputs/RangeCalendar/RangeCalendar";
import { Icon, IconType } from "../../common/icons/Icon";
import { SkeletonItem } from "../../common/components/skeletons/SkeletonItem";
import { usePageGuide } from "../onboarding/hooks/usePageGuide";
import { OnboardingItemKey } from "../../common/enums";
import styles from "./GymPage.module.scss";

interface IGymHistoryRangeForm {
  dateFrom: Date | null;
  dateTo: Date | null;
}

const toApiDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

// Backend returns DateTime values without a timezone marker (Kind=Unspecified
// after EF reads from datetime2). They are UTC, so we append "Z" before parsing
// so JS doesn't interpret them as local time. Same workaround as
// AuditTimeline.tsx and userAuditLogUtility.ts.
const parseUtc = (iso: string) =>
  new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z");

const formatTime = (iso: string) => {
  return parseUtc(iso).toLocaleTimeString("bg-BG", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (
  fromIso: string,
  toIso: string | null,
  nowMs: number,
) => {
  const from = parseUtc(fromIso).getTime();
  const to = toIso ? parseUtc(toIso).getTime() : nowMs;
  const totalMinutes = Math.max(0, Math.floor((to - from) / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}м`;
  return `${hours}ч ${minutes}м`;
};

const planStatusPill = (member: IGymUserSearchResult) => {
  if (!member.activePlan) {
    return { className: styles.pill_none, label: "Няма план" };
  }
  return { className: styles.pill_unlimited, label: "Има план" };
};

const planSummaryText = (member: IGymUserSearchResult) => {
  if (!member.activePlan) {
    return {
      label: "План",
      value: "Няма активен план",
      sub: "Изисква плащане на място",
    };
  }
  if (member.activePlan.isUnlimited) {
    return {
      label: "План",
      value: "Безлимитен",
      sub: member.activePlan.planName,
    };
  }
  const remaining = member.activePlan.remainingVisits ?? 0;
  return {
    label: "План",
    value:
      remaining === 1 ? "Остава 1 посещение" : `Остават ${remaining} посещения`,
    sub: member.activePlan.planName,
  };
};

export const GymPage = () => {
  const navigate = useNavigate();
  const {
    stats,
    query,
    setQuery,
    isDropdownOpen,
    setDropdownOpen,
    searchResults,
    selectedMember,
    openVisitForSelected,
    insideVisits,
    historyItems,
    historyTotalCount,
    historyHasMore,
    historyPage,
    historyTotalPages,
    historyFilter,
    setCustomDateFrom,
    setCustomDateTo,
    handleSetHistoryFilter,
    handlePrevPage,
    handleNextPage,
    handleSelectMember,
    handleNewUserCreated,
    handleToggleCheckIn,
    handleRevertLastAction,
    canRevertLastAction,
    isRevertLoading,
    isCheckInSubmitting,
    pendingPaymentFor,
    isPaymentSubmitting,
    handleConfirmPayment,
    handleCancelPayment,
    paymentOptions,
    multisportPercentLeft,
    coolFitPercentLeft,
    isHistoryLoading,
  } = useGym();

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [showNewUserDialog, setShowNewUserDialog] = useState(false);

  usePageGuide(OnboardingItemKey.GymVisitsGuide);

  // Local form context so we can use the shared RangeCalendar for the
  // history "По дата" filter — it requires react-hook-form context.
  const dateRangeForm = useForm<IGymHistoryRangeForm>({
    defaultValues: { dateFrom: null, dateTo: null },
  });

  // Only fire the API request once BOTH range endpoints are picked — otherwise
  // PrimeReact's first-click intermediate state (dateTo=null) would trigger a
  // half-filtered fetch.
  useEffect(() => {
    const sub = dateRangeForm.watch((values) => {
      if (values.dateFrom && values.dateTo) {
        setCustomDateFrom(toApiDate(values.dateFrom));
        setCustomDateTo(toApiDate(values.dateTo));
      }
    });
    return () => sub.unsubscribe();
  }, [dateRangeForm, setCustomDateFrom, setCustomDateTo]);

  // Tick every 30 s so live durations (open visits) update without a refetch.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  // Keep the search input focused — RFID readers emulate a keyboard, so the
  // search field must always be ready to catch a scan.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [setDropdownOpen]);

  const isSelectedInside = !!openVisitForSelected;

  // Backend returns history rows ordered by CheckedInAt DESC, so render as-is.
  const filterPills: { id: HistoryFilter; label: string }[] = [
    { id: "today", label: "Днес" },
    { id: "thisWeek", label: "Тази седмица" },
    { id: "dateRange", label: "По дата" },
    { id: "inside", label: "Вътре сега" },
    { id: "all", label: "Всички" },
  ];

  const skeletonColumns: { label: string; col: string; width: string }[] = [
    { label: "Потребител", col: "user", width: "70%" },
    { label: "RFID", col: "rfid", width: "55%" },
    { label: "Влизане", col: "in", width: "45%" },
    { label: "Излизане", col: "out", width: "45%" },
    { label: "Продължителност", col: "duration", width: "50%" },
    { label: "Плащане", col: "payment", width: "55%" },
    { label: "Заплатено", col: "paid", width: "45%" },
    { label: "Поредно посещение", col: "visit", width: "40%" },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.header_title}>
          <span className={styles.header_icon}>🏋️</span>
          Зала
        </div>
        <div className={styles.stats}>
          <div className={styles.stat}>
            Вътре сега <strong>{stats.insideNow}</strong>
          </div>
          <div className={styles.stat}>
            Посещения днес <strong>{stats.today}</strong>
          </div>
          <div className={styles.stat}>
            Капацитет <strong>{stats.insideNow}/∞</strong>
          </div>
        </div>
      </div>

      <div className={styles.searchRow}>
        <div
          className={styles.searchWrapper}
          ref={wrapperRef}
          data-onboarding="gym-search"
        >
          <span className={styles.searchIcon}>🔍</span>
          <input
            ref={inputRef}
            type="text"
            className={styles.searchInput}
            placeholder="Сканирай карта или търси по ID, име, телефон, имейл, RFID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.trim() && setDropdownOpen(true)}
            autoFocus
          />
          {isDropdownOpen && searchResults.length > 0 && (
            <div className={styles.dropdown}>
              {searchResults.map((m) => {
                const pill = planStatusPill(m);
                const insideAlready = !!m.openVisitId;
                return (
                  <div
                    key={m.id}
                    className={styles.dropdownItem}
                    onClick={() => handleSelectMember(m.id)}
                  >
                    <div className={styles.dropdownAvatar}>
                      {m.profilePhotoUrl ? (
                        <img
                          src={m.profilePhotoUrl}
                          alt=""
                          className={styles.avatarImg}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        getInitials(getMemberFullName(m))
                      )}
                    </div>
                    <div className={styles.dropdownInfo}>
                      <div className={styles.dropdownName}>
                        {getMemberFullName(m)}
                      </div>
                      <div className={styles.dropdownMeta}>
                        {[m.phoneNumber, m.email].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <div className={styles.dropdownRfidCell}>
                      {m.rfidBandId && (
                        <span className={styles.rfidChip}>{m.rfidBandId}</span>
                      )}
                    </div>
                    <div className={styles.dropdownStatusCell}>
                      {insideAlready ? (
                        <span
                          className={classNames(
                            styles.pill,
                            styles.pill_inside,
                          )}
                        >
                          Вече е в залата
                        </span>
                      ) : (
                        <span
                          className={classNames(styles.pill, pill.className)}
                        >
                          {pill.label}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <button
          type="button"
          className={styles.addUserBtn}
          onClick={() => setShowNewUserDialog(true)}
          title="Добави нов потребител"
          data-onboarding="gym-add-user"
        >
          <Icon type={IconType.Plus2} />
        </button>
      </div>

      <div className={styles.grid}>
        <div className={styles.card}>
          {selectedMember ? (
            <>
              <div className={styles.memberHeader}>
                <div className={styles.memberAvatar}>
                  {selectedMember.profilePhotoUrl ? (
                    <img
                      src={selectedMember.profilePhotoUrl}
                      alt=""
                      className={styles.avatarImg}
                      decoding="async"
                    />
                  ) : (
                    getInitials(getMemberFullName(selectedMember))
                  )}
                </div>
                <div className={styles.memberIdentity}>
                  <div className={styles.memberTopRow}>
                    <div className={styles.memberName}>
                      {getMemberFullName(selectedMember)}
                    </div>
                    {selectedMember.rfidBandId && (
                      <span
                        className={classNames(
                          styles.rfidChip,
                          styles.memberRfid,
                        )}
                      >
                        {selectedMember.rfidBandId}
                      </span>
                    )}
                  </div>
                  <div className={styles.memberContact}>
                    {selectedMember.phoneNumber && (
                      <span>📱 {selectedMember.phoneNumber}</span>
                    )}
                    {selectedMember.email && (
                      <span>📧 {selectedMember.email}</span>
                    )}
                  </div>
                </div>
              </div>

              {(() => {
                const summary = planSummaryText(selectedMember);
                return (
                  <div className={styles.planBox}>
                    <div className={styles.planBox_label}>{summary.label}</div>
                    <div className={styles.planBox_value}>{summary.value}</div>
                    {summary.sub && (
                      <div className={styles.planBox_sub}>{summary.sub}</div>
                    )}
                  </div>
                );
              })()}

              <div className={styles.actions}>
                <button
                  className={classNames(
                    styles.btn,
                    isSelectedInside ? styles.btn_success : styles.btn_primary,
                  )}
                  onClick={() => handleToggleCheckIn(selectedMember.id)}
                  disabled={isCheckInSubmitting || isRevertLoading}
                >
                  {isCheckInSubmitting && (
                    <span className={styles.btn_spinner} />
                  )}
                  <span>
                    {isSelectedInside
                      ? "⬅️ Отбележи излизане"
                      : "➡️ Отбележи влизане"}
                  </span>
                </button>
                <button
                  className={classNames(styles.btn, styles.btn_secondary)}
                  onClick={handleRevertLastAction}
                  disabled={
                    !canRevertLastAction ||
                    isRevertLoading ||
                    isCheckInSubmitting
                  }
                >
                  {isRevertLoading && (
                    <span className={styles.btn_spinner} />
                  )}
                  <span>↩️ Отмени последно сканиране</span>
                </button>
                <button
                  className={classNames(styles.btn, styles.btn_secondary)}
                  onClick={() =>
                    navigate(
                      constructCashdeskHref({ userId: selectedMember.id }),
                    )
                  }
                >
                  💳 Каса
                </button>
                <button
                  className={classNames(styles.btn, styles.btn_secondary)}
                  onClick={() =>
                    navigate(constructEditUserHref(selectedMember.id))
                  }
                >
                  ✏️ Редактирай профил
                </button>
              </div>
            </>
          ) : (
            <div
              className={styles.memberCard_placeholder}
              data-onboarding="gym-profile-placeholder"
            >
              <div>👤</div>
              <div>Избери потребител за да видиш профила му.</div>
            </div>
          )}
        </div>

        <div className={styles.card}>
          <div className={styles.insideSection_header}>
            <h3 data-onboarding="gym-inside-header">
              Трениращи в момента ({insideVisits.length})
            </h3>
          </div>
          {insideVisits.length === 0 ? (
            <div className={styles.emptyInside}>Няма потребители в залата.</div>
          ) : (
            <div className={styles.insideGrid}>
              {insideVisits.map((v: IGymVisitViewModel) => (
                <div
                  key={v.id}
                  className={styles.insideItem}
                  onClick={() => handleSelectMember(v.userId)}
                >
                  <div className={styles.insideItem_avatar}>
                    {v.profilePhotoUrl ? (
                      <img
                        src={v.profilePhotoUrl}
                        alt=""
                        className={styles.avatarImg}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      getInitials(v.userFullName)
                    )}
                  </div>
                  <div className={styles.insideItem_name}>{v.userFullName}</div>
                  <div className={styles.insideItem_time}>
                    от {formatTime(v.checkedInAt)} ·{" "}
                    {formatDuration(v.checkedInAt, null, nowMs)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.historySection_header}>
          <h3>История на посещенията</h3>
          <span className={styles.historySection_count}>
            {historyTotalCount} записа
          </span>
        </div>

        <div className={styles.filterPills}>
          {filterPills.map((p) => (
            <button
              key={p.id}
              className={classNames(styles.filterPill, {
                [styles.filterPill_active]: historyFilter === p.id,
              })}
              onClick={() => handleSetHistoryFilter(p.id)}
              type="button"
            >
              {p.label}
            </button>
          ))}
        </div>

        {historyFilter === "dateRange" && (
          <div className={styles.dateRange}>
            <FormProvider {...dateRangeForm}>
              <RangeCalendar
                label="Период"
                dateFromName="dateFrom"
                dateToName="dateTo"
                maxDate={new Date()}
                width="220px"
              />
            </FormProvider>
          </div>
        )}

        <div className={styles.historyTableWrapper}>
          <table className={styles.historyTable}>
            <thead>
              <tr>
                <th>Потребител</th>
                <th>RFID</th>
                <th>Влизане</th>
                <th>Излизане</th>
                <th>Продължителност</th>
                <th>Плащане</th>
                <th>Заплатено</th>
                <th>Поредно посещение</th>
              </tr>
            </thead>
            <tbody>
              {isHistoryLoading
                ? Array.from({ length: 6 }).map((_, rowIdx) => (
                    <tr key={`skeleton-${rowIdx}`}>
                      {skeletonColumns.map((c) => (
                        <td
                          key={c.col}
                          data-label={c.label}
                          data-col={c.col}
                        >
                          <SkeletonItem
                            isLoading
                            value=""
                            width={c.width}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                : historyItems.map((v: IGymVisitViewModel) => (
                    <tr key={v.id}>
                      <td data-label="Потребител" data-col="user">
                        <span
                          className={classNames(
                            styles.statusDot,
                            v.checkedOutAt === null
                              ? styles.statusDot_inside
                              : styles.statusDot_left,
                          )}
                        />
                        {v.userFullName}
                      </td>
                      <td data-label="RFID" data-col="rfid">
                        {v.rfidBandId ? (
                          <span className={styles.rfidChip}>
                            {v.rfidBandId}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td data-label="Влизане" data-col="in">
                        {formatTime(v.checkedInAt)}
                      </td>
                      <td data-label="Излизане" data-col="out">
                        {v.checkedOutAt
                          ? formatTime(v.checkedOutAt)
                          : "в залата"}
                      </td>
                      <td data-label="Продължителност" data-col="duration">
                        {formatDuration(v.checkedInAt, v.checkedOutAt, nowMs)}
                      </td>
                      <td data-label="Плащане" data-col="payment">
                        {v.paymentType
                          ? (humanizePaymentType(
                              v.paymentType,
                              v.subscriptionPlanId ?? undefined,
                              v.planName,
                            ) ?? "—")
                          : "—"}
                      </td>
                      <td data-label="Заплатено" data-col="paid">
                        {v.pricePaid != null
                          ? `${v.pricePaid.toFixed(2)} €`
                          : "—"}
                      </td>
                      <td data-label="Поредно посещение" data-col="visit">
                        {v.visitIndex != null && v.planInitialVisits != null
                          ? `${v.visitIndex}/${v.planInitialVisits}`
                          : "—"}
                      </td>
                    </tr>
                  ))}
              {!isHistoryLoading && historyItems.length === 0 && (
                <tr className={styles.historyTable_emptyRow}>
                  <td colSpan={8}>
                    Няма записани посещения за избрания период.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {historyTotalCount > 0 && (
          <div className={styles.pagination}>
            <button
              type="button"
              className={classNames(
                styles.pagination_btn,
                styles.pagination_btn_prev,
              )}
              disabled={historyPage <= 1}
              onClick={handlePrevPage}
            >
              <span className={styles.pagination_btn_arrow}>←</span>
              <span className={styles.pagination_btn_label}>Предишна</span>
            </button>
            <span className={styles.pagination_info}>
              Страница {historyPage} от {historyTotalPages}
            </span>
            <button
              type="button"
              className={styles.pagination_btn}
              disabled={!historyHasMore}
              onClick={handleNextPage}
            >
              <span className={styles.pagination_btn_label}>Следваща</span>
              <span className={styles.pagination_btn_arrow}>→</span>
            </button>
          </div>
        )}
      </div>

      <GymPaymentModal
        visible={!!pendingPaymentFor}
        member={pendingPaymentFor}
        defaultPrice={15}
        onConfirm={handleConfirmPayment}
        onCancel={handleCancelPayment}
        isSubmitting={isPaymentSubmitting}
        paymentOptions={paymentOptions}
        multisportPercentLeft={multisportPercentLeft}
        coolFitPercentLeft={coolFitPercentLeft}
      />

      <AppDialog
        header="Добавяне на нов потребител"
        visible={showNewUserDialog}
        onHide={() => setShowNewUserDialog(false)}
      >
        <UserWizardContents
          pageMode={"add"}
          onCreated={(newUserId) => {
            setShowNewUserDialog(false);
            handleNewUserCreated(newUserId);
          }}
          hideFontSize
          hideScheduleVisibility
        />
      </AppDialog>
    </div>
  );
};
